import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useState, useEffect } from "react";
import { usePaymentTerms, useGetCustomerPaymentTerms } from "@/api/payments";
import { useUpdateCustomerPaymentAccess } from "@/api/customers";
import { toast } from "sonner";
import { Loader2, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface PaymentAccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer: {
    id: string;
    firstName?: string;
    lastName?: string;
    allowedPaymentTermsIds?: string[];
    netTermsCreditLimit?: number | null;
  } | null;
}

export function PaymentAccessDialog({
  open,
  onOpenChange,
  customer,
}: PaymentAccessDialogProps) {
  const [selectedPaymentTermsIds, setSelectedPaymentTermsIds] = useState<
    string[]
  >([]);
  const [creditLimit, setCreditLimit] = useState<string>("");

  const { data: paymentTerms, isLoading: termsLoading } = usePaymentTerms();
  const { data: customerPaymentInfo } = useGetCustomerPaymentTerms(
    customer?.id || ""
  );
  const updatePaymentAccess = useUpdateCustomerPaymentAccess();

  // Initialize form when customer changes
  useEffect(() => {
    if (customer) {
      setSelectedPaymentTermsIds(customer.allowedPaymentTermsIds ?? []);
      setCreditLimit(
        customer.netTermsCreditLimit
          ? (customer.netTermsCreditLimit / 100).toString()
          : ""
      );
    }
  }, [customer]);

  const handleSave = async () => {
    if (!customer) return;

    try {
      await updatePaymentAccess.mutateAsync({
        customerId: customer.id,
        allowedPaymentTermsIds: selectedPaymentTermsIds,
        netTermsCreditLimit: creditLimit
          ? Math.round(parseFloat(creditLimit) * 100)
          : undefined,
      });

      toast.success("Payment access updated", {
        description:
          "Customer payment access settings have been updated successfully.",
      });

      onOpenChange(false);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(
        err.response?.data?.error ?? "Failed to update payment access"
      );
    }
  };

  const togglePaymentTerm = (termId: string) => {
    setSelectedPaymentTermsIds((prev) =>
      prev.includes(termId)
        ? prev.filter((id) => id !== termId)
        : [...prev, termId]
    );
  };

  const customerName =
    customer?.firstName && customer?.lastName
      ? `${customer.firstName} ${customer.lastName}`
      : "Customer";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>Manage Payment Access</DialogTitle>
          <DialogDescription>
            Configure NET terms access and credit limits for {customerName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Unpaid Invoices Warning */}
          {customerPaymentInfo?.hasUnpaidInvoices && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Customer has {customerPaymentInfo.unpaidInvoiceCount} unpaid
                invoice(s). They cannot place new NET terms orders until
                invoices are paid.
              </AlertDescription>
            </Alert>
          )}

          {/* Payment Terms Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">
              Allowed Payment Terms (NET terms)
            </Label>
            {termsLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading payment terms...
              </div>
            ) : (
              <div className="space-y-2">
                {paymentTerms && paymentTerms.length > 0 ? (
                  paymentTerms
                    .filter((term) => term.isActive)
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((term) => (
                      <div
                        key={term.id}
                        className="flex items-center space-x-2 p-2 rounded-md hover:bg-accent"
                      >
                        <Checkbox
                          id={`term-${term.id}`}
                          checked={selectedPaymentTermsIds.includes(term.id)}
                          onCheckedChange={() => togglePaymentTerm(term.id)}
                        />
                        <label
                          htmlFor={`term-${term.id}`}
                          className="flex-1 text-sm cursor-pointer"
                        >
                          <div className="font-medium">{term.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {term.code} - Due in {term.paymentDueDays} days
                            {term.description && ` • ${term.description}`}
                          </div>
                        </label>
                      </div>
                    ))
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No payment terms available
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Credit Limit */}
          <div className="space-y-2">
            <Label htmlFor="creditLimit">NET Terms Credit Limit</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                $
              </span>
              <Input
                id="creditLimit"
                type="text"
                inputMode="decimal"
                placeholder="0.00"
                value={creditLimit}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
                    setCreditLimit(v);
                  }
                }}
                className="pl-6"
                disabled={selectedPaymentTermsIds.length === 0}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Maximum outstanding balance allowed for NET terms orders. Defaults
              to $0 (no credit).
            </p>
            {customerPaymentInfo && (
              <div className="mt-2 p-3 bg-muted rounded-md space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Current Credit Limit:
                  </span>
                  <span className="font-medium">
                    ${(customerPaymentInfo.creditLimit / 100).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Outstanding Balance:
                  </span>
                  <span className="font-medium">
                    ${(customerPaymentInfo.outstandingBalance / 100).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Available Credit:
                  </span>
                  <span className="font-medium text-green-600">
                    ${(customerPaymentInfo.availableCredit / 100).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Info */}
          {selectedPaymentTermsIds.length > 0 && (
            <div className="rounded-md bg-blue-50 dark:bg-blue-950/20 p-3 text-xs text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Payment Access Enabled</p>
              <p>
                This customer can now create orders with the selected NET terms.
                Invoices will be generated based on the payment terms
                configuration.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updatePaymentAccess.isPending}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updatePaymentAccess.isPending}>
            {updatePaymentAccess.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
